import { useId, type ReactNode } from 'react';

import type { EmptyStateProps } from './types';
import { emptyStateStyles } from './styles';

/**
 * "There is nothing here" — and nothing is wrong.
 *
 * That distinction is the whole component. A list that loaded successfully and
 * matched nothing looks exactly like a list that failed to load unless the
 * surface says which one the operator is looking at, and an operator who cannot
 * tell will retry, or escalate, or assume their own filters are wrong.
 *
 * `role="status"` is a POLITE live region: an empty state appears as a result of
 * a completed action, so it is announced when it arrives without interrupting
 * whatever the operator is doing. An error is not polite and does not use this.
 *
 * The accessible name is wired with `aria-labelledby` rather than a literal
 * `aria-label` so that the name IS the visible title. A duplicated string has to
 * be kept in sync by hand and drifts; a label that points at the element cannot.
 */
export function EmptyState({ title, description, action }: EmptyStateProps): ReactNode {
  const titleId = useId();

  return (
    <div role="status" aria-labelledby={titleId} className={emptyStateStyles.root}>
      <p id={titleId} className={emptyStateStyles.title}>
        {title}
      </p>

      {description === undefined ? null : (
        <p className={emptyStateStyles.description}>{description}</p>
      )}

      {action === undefined ? null : <div className={emptyStateStyles.action}>{action}</div>}
    </div>
  );
}
